"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

/* Representação visual do checkout Shoppub: um loop autônomo que
   percorre as 3 etapas reais (dados → entrega → pagamento) e conclui,
   com cronômetro. Dados fictícios (Marina). */

const EMAIL = "marina@exemplo.com.br";
const ETAPAS = ["Seus dados", "Sua entrega", "Seu pagamento"];
const ETAPAS_CURTAS = ["Dados", "Entrega", "Pagamento"];

type Fase = "digitando" | "entrega" | "pagamento" | "concluido";

export default function MockCheckout() {
  const raiz = useRef<HTMLDivElement>(null);
  const [on, setOn] = useState(false);
  const [fase, setFase] = useState<Fase>("digitando");
  const [chars, setChars] = useState(0);
  const [cron, setCron] = useState(0);

  useEffect(() => {
    const el = raiz.current;
    if (!el) return;
    if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
      setOn(true);
      return;
    }
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && (setOn(true), io.disconnect()),
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // roteiro do loop
  useEffect(() => {
    if (!on) return;
    let vivo = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const t = (fn: () => void, ms: number) => timers.push(setTimeout(() => vivo && fn(), ms));

    const rodada = () => {
      setFase("digitando");
      setChars(0);
      setCron(0);
      // digitação
      for (let i = 1; i <= EMAIL.length; i++) t(() => setChars(i), 400 + i * 90);
      t(() => setFase("entrega"), 3400);
      t(() => setFase("pagamento"), 6600);
      t(() => setFase("concluido"), 9800);
      t(rodada, 13400);
    };
    rodada();

    const c = setInterval(() => {
      if (vivo) setCron((x) => x + 0.1);
    }, 100);

    return () => {
      vivo = false;
      timers.forEach(clearTimeout);
      clearInterval(c);
    };
  }, [on]);

  const etapaAtiva = fase === "digitando" ? 0 : fase === "entrega" ? 1 : 2;
  const cronExibe = fase === "concluido" ? "19,4" : Math.min(cron, 19.4).toFixed(1).replace(".", ",");

  return (
    <div ref={raiz} className="select-none rounded-card bg-paper p-5 text-ink shadow-[0_40px_90px_-40px_rgba(0,0,0,0.7)] sm:p-6">
      {/* topo: marca fictícia + segurança + cronômetro */}
      <div className="flex items-center justify-between border-b border-line pb-3.5">
        <Image src="/brand/logo_shoppub_preto.svg" alt="Shoppub" width={110} height={31} className="h-7 w-auto" />
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-ink/70">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="5" y="10" width="14" height="10" rx="2" />
              <path d="M8 10V7a4 4 0 018 0v3" />
            </svg>
            Compra segura
          </span>
          <span
            className={`rounded-full px-2.5 py-1 font-mono text-[11px] font-bold ${
              fase === "concluido" ? "bg-lime text-ink" : "bg-mist text-ink/70"
            }`}
          >
            {cronExibe}s
          </span>
        </span>
      </div>

      {fase !== "concluido" ? (
        <>
          {/* abas das etapas */}
          <div className="mt-4 flex gap-4 whitespace-nowrap border-b border-line text-[12px] font-bold sm:gap-5">
            {ETAPAS.map((e, i) => (
              <span
                key={e}
                className={`-mb-px border-b-2 pb-2 transition-colors duration-300 ${
                  i === etapaAtiva
                    ? "border-ink text-ink"
                    : i < etapaAtiva
                      ? "border-transparent text-lime-deep"
                      : "border-transparent text-ink/60"
                }`}
              >
                {i + 1}. <span className="sm:hidden">{ETAPAS_CURTAS[i]}</span>
                <span className="hidden sm:inline">{e}</span> {i < etapaAtiva && "✓"}
              </span>
            ))}
          </div>

          <div className="mt-5 min-h-[240px]">
            {fase === "digitando" && (
              <div>
                <div className="text-center text-[13px] font-bold uppercase tracking-wider text-ink/80">
                  Identificação
                </div>
                <p className="mt-1 text-center text-[12px] text-ink/70">
                  Sem senha — informe seu e-mail ou CPF para continuar
                </p>
                <div className="mt-4 flex gap-2">
                  <div className="flex h-11 flex-1 items-center overflow-hidden rounded-xl border border-ink/25 px-3.5 text-[13px]">
                    {EMAIL.slice(0, chars)}
                    <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-ink" />
                  </div>
                  <span className="flex h-11 items-center rounded-xl bg-ink px-4 text-[12px] font-bold text-paper">
                    Continuar
                  </span>
                </div>
                <div className="mt-3 flex h-11 items-center justify-center gap-2 rounded-xl border border-line text-[13px] font-semibold text-ink/75">
                  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
                    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2.1 3.7-5.1 3.7-8.6z" />
                    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.8-3c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-6.9-5.2H1.2v3C3.2 21.1 7.3 24 12 24z" />
                    <path fill="#FBBC05" d="M5.1 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1v-3H1.2C.4 8.4 0 10.2 0 12s.4 3.6 1.2 5.1l3.9-3z" />
                    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l2.7-2.7C16.6 1.3 14 0 12 0 7.3 0 3.2 2.9 1.2 6.9l3.9 3C6 6.9 8.8 4.7 12 4.7z" />
                  </svg>
                  Fazer login com o Google
                </div>
                <p className="mt-3 text-center text-[10px] text-ink/70">
                  Seus dados são criptografados — compra 100% segura
                </p>
              </div>
            )}

            {fase === "entrega" && (
              <div className="flex flex-col gap-2.5">
                <div className="rounded-xl bg-mist px-3.5 py-2 text-[12px] text-smoke">
                  Olá, <b className="text-ink">Marina</b> — endereço e dados carregados
                  automaticamente ✓
                </div>
                <div className="flex items-start gap-3 rounded-xl border-2 border-ink p-3.5">
                  <span className="mt-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-ink">
                    <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="#c9f158" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12.5l4.2 4.2L19 7" />
                    </svg>
                  </span>
                  <span className="text-[13px] leading-snug">
                    <b>Endereço principal</b>
                    <span className="block text-smoke">
                      Rua das Acácias, 128 — Pinheiros · São Paulo, SP
                    </span>
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-xl border-2 border-ink p-3.5">
                  <span className="text-[13px]">
                    <b>Entrega expressa</b>
                    <span className="block text-smoke">até 5 dias úteis</span>
                  </span>
                  <span className="text-[13px] font-bold">
                    <s className="mr-1.5 font-normal text-smoke">R$ 17,90</s>
                    <span className="text-lime-deep">Grátis</span>
                  </span>
                </div>
              </div>
            )}

            {fase === "pagamento" && (
              <div className="flex flex-col gap-2.5">
                <div className="flex items-center justify-between rounded-xl border-2 border-ink p-3.5">
                  <span className="flex items-center gap-2.5 text-[13px]">
                    <span className="mt-0 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-ink">
                      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="#c9f158" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12.5l4.2 4.2L19 7" />
                      </svg>
                    </span>
                    <b>Pix</b>
                    <span className="rounded-full bg-lime px-2 py-0.5 text-[10px] font-bold">
                      −5% à vista
                    </span>
                  </span>
                  <span className="text-[13px] font-bold">R$ 473,10</span>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-line p-3.5 text-[13px] text-smoke">
                  <span>Cartão de crédito · até 10×</span>
                  <span>R$ 498,00</span>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-line p-3.5 text-[13px] text-smoke">
                  <span>Boleto à vista</span>
                  <span>R$ 498,00</span>
                </div>
                <div className="mt-1 flex items-center justify-between border-t border-line pt-3 text-[13px]">
                  <span className="text-smoke">Vestido Amara · M</span>
                  <span className="font-display text-lg font-bold">Total R$ 473,10</span>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="flex min-h-[292px] flex-col items-center justify-center text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-lime">
            <svg viewBox="0 0 24 24" className="h-8 w-8" fill="none" stroke="#0a0a0b" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12.5l4.2 4.2L19 7" />
            </svg>
          </span>
          <div className="mt-4 font-display text-2xl font-bold">Pedido confirmado</div>
          <p className="mt-1 text-[13px] text-smoke">
            Pedido nº 00087 412 · Pix aprovado
          </p>
          <p className="mt-4 rounded-full bg-mist px-4 py-1.5 text-[12px] font-bold">
            do carrinho ao pago em <span className="text-lime-deep">19,4 segundos</span>
          </p>
        </div>
      )}
    </div>
  );
}
